/**
 * FRONT-703 (follow-up to FRONT-694) — pure decision logic for the deep-research
 * surface, extracted out of inbox-chat-modal.tsx so it can be unit-tested
 * without React.
 *
 * ## FRONT-1500 (fifth pass): there is only one decision left here
 *
 * There used to be a second one — whether the user had acknowledged the
 * explanatory copy ("Got it"), persisted per run id. That whole concept is
 * gone. Acknowledging a sentence used to collapse the run's live work log,
 * source list and Stop button along with it, which is the black box this
 * surface exists to remove; the fix for that was to make the sentence
 * separately collapsible, and the fix for THAT (the product owner's own
 * report: the live detail was cramped into a band above the chat) was to give
 * the whole detail panel the full conversation area.
 *
 * (FRONT-1578 correction: at the time this was written the panel only ever
 * opened "on demand from the bar" — a click. That's no longer the whole
 * story: the panel now opens ITSELF the instant a run's id is confirmed, and
 * folds itself away the instant the user sends a message — see
 * `research-panel-open.ts`'s `nextResearchPanelState`, a SIBLING module, not
 * a change to this one. That module is also where "keyed by run id" now
 * lives — see its own docblock for why a plain open/closed boolean can't
 * express "the user closed this" on its own.)
 *
 * Open/closed is therefore transient view state owned by the modal — not an
 * acknowledgement, nothing to persist, and nothing that can hide the fact
 * that a run is going. What remains here is the
 * only durable question: is there a run to show a bar for at all?
 *
 * ## The optimistic-window subtlety (FRONT-703)
 *
 * The bar shows optimistically the instant a deep-research plan is approved,
 * before the real research run id is known — otherwise the chat looks like
 * nothing happened for the ~1s until the run row round-trips back. The caller
 * clears that flag when the real run appears, or after a short timeout if a
 * run never materialises (a failed launch must not leave a bar stuck on).
 */
export function isResearchBannerVisible({
  runningResearchId,
  optimistic,
}: {
  /** The real research run id, or null if it hasn't arrived (or none is running). */
  runningResearchId: string | null;
  /** The optimistic "just approved, run not confirmed yet" flag. */
  optimistic: boolean;
}): boolean {
  return runningResearchId !== null || optimistic;
}
